import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ActivityIcon, AlertTriangleIcon, ClipboardCheckIcon, FileClockIcon, HeadphonesIcon, RocketIcon, ShieldAlertIcon, UsersIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { ScopeSwitcher } from '../../components/admin/ScopeSwitcher';
import { KpiCard } from '../../components/admin/KpiCard';
import { WidgetBoundary } from '../../components/admin/Widget';
import { ActivityWidget } from '../../components/admin/widgets/ActivityWidget';
import { ActivityChartWidget } from '../../components/admin/widgets/ActivityChartWidget';
import { HealthWidget } from '../../components/admin/widgets/HealthWidget';
import { QuickActionsWidget } from '../../components/admin/widgets/QuickActionsWidget';
import { DesignVariantWidget } from '../../components/admin/widgets/DesignVariantWidget';
import { useGovernance } from '../../contexts/GovernanceContext';
import { useTickets } from '../../contexts/TicketContext';
import { allCampuses, evaluateLaunch, scopeCovers } from '../../utils/governance';
import { auditInScope } from '../../utils/auditScope';

/**
 * Admin home answers one question: what needs my attention?
 *
 * Every row is permission-aware and scope-aware, so two administrators signing
 * in see genuinely different work rather than the same list with disabled rows.
 */
export function AdminHome() {
  const {
    approvals,
    staff,
    invitations,
    switches,
    audit,
    launchChecks,
    tenants,
    activeScope,
    allows,
    me,
    scopeLabel
  } = useGovernance();
  const {
    tickets
  } = useTickets();
  const navigate = useNavigate();
  const pendingApprovals = approvals.filter(request => request.state === 'requested' && scopeCovers(activeScope, request.scope));
  const canSeeApprovals = allows('staff.approve').allowed || allows('payouts.approve').allowed;
  const expiredAccess = staff.filter(member => member.assignments.some(assignment => assignment.state === 'active' && assignment.expiresAt && new Date(assignment.expiresAt) < new Date()));
  const noMfa = staff.filter(member => member.status === 'active' && !member.mfaEnabled);
  const pendingInvites = invitations.filter(invitation => invitation.status === 'pending');
  const expiredInvites = invitations.filter(invitation => invitation.status === 'expired');
  const paused = switches.filter(item => item.state === 'paused');
  const openTickets = tickets.filter(ticket => ticket.status !== 'Resolved' && ticket.status !== 'Closed');
  const deniedAttempts = audit.filter(record => record.result === 'denied');
  const activeStaff = staff.filter(member => member.status === 'active');
  const deniedInScope = deniedAttempts.filter(record => auditInScope(record.scope, activeScope, scopeLabel(activeScope)));

  // Fourteen daily counts of refused attempts, counted from the audit log. Not a
  // trend line — a shape, beside a number that is the actual value.
  const midnight = new Date();
  midnight.setHours(0, 0, 0, 0);
  const deniedSeries = Array.from({
    length: 14
  }, (_, index) => {
    const day = new Date(midnight);
    day.setDate(midnight.getDate() - (13 - index));
    const next = new Date(day);
    next.setDate(day.getDate() + 1);
    return deniedInScope.filter(record => {
      const at = new Date(record.at);
      return at >= day && at < next;
    }).length;
  });
  const launchBlocked = allCampuses(tenants).filter(({
    tenant,
    campus
  }) => scopeCovers(activeScope, {
    level: 'campus',
    tenantId: tenant.id,
    campusId: campus.id,
    institutionId: tenant.institutions.find(institution => institution.campuses.some(item => item.id === campus.id))?.id
  })).map(({
    campus
  }) => evaluateLaunch(campus, launchChecks)).filter(outcome => outcome.campus.status !== 'live' && outcome.blockers.length > 0);
  const attention = [canSeeApprovals && pendingApprovals.length > 0 ? {
    id: 'approvals',
    tone: 'warn' as const,
    icon: ClipboardCheckIcon,
    title: `${pendingApprovals.length} request${pendingApprovals.length === 1 ? '' : 's'} waiting on an approver`,
    detail: pendingApprovals.map(request => request.summary).join(' · '),
    to: '/admin/governance',
    action: 'Open approvals'
  } : null, launchBlocked.length > 0 ? {
    id: 'launch',
    tone: 'danger' as const,
    icon: RocketIcon,
    title: `${launchBlocked.length} campus${launchBlocked.length === 1 ? '' : 'es'} cannot go live`,
    detail: launchBlocked.map(outcome => `${outcome.campus.name}: ${outcome.blockers.length} blocker${outcome.blockers.length === 1 ? '' : 's'}`).join(' · '),
    to: '/admin/launch',
    action: 'Open launch control'
  } : null, expiredAccess.length > 0 ? {
    id: 'expired',
    tone: 'warn' as const,
    icon: ShieldAlertIcon,
    title: `${expiredAccess.length} staff assignment${expiredAccess.length === 1 ? '' : 's'} past expiry`,
    detail: 'Access already stopped working. The records still need reviewing or renewing so the person is not silently locked out.',
    to: '/admin/governance',
    action: 'Open access review'
  } : null, noMfa.length > 0 ? {
    id: 'mfa',
    tone: 'warn' as const,
    icon: ShieldAlertIcon,
    title: `${noMfa.length} active administrator without MFA`,
    detail: noMfa.map(member => member.handle).join(' · '),
    to: '/admin/governance',
    action: 'Open staff'
  } : null, deniedAttempts.length > 0 ? {
    id: 'denied',
    tone: 'neutral' as const,
    icon: FileClockIcon,
    title: `${deniedAttempts.length} refused administrative attempt${deniedAttempts.length === 1 ? '' : 's'}`,
    detail: 'Refusals are logged like successes. Repeated attempts on the same record are worth a look.',
    to: '/admin/audit',
    action: 'Open audit log'
  } : null, openTickets.length > 0 ? {
    id: 'tickets',
    tone: 'brand' as const,
    icon: ActivityIcon,
    title: `${openTickets.length} open support ticket${openTickets.length === 1 ? '' : 's'}`,
    detail: openTickets.map(ticket => ticket.subject).slice(0, 2).join(' · '),
    to: '/admin/support',
    action: 'Open support desk'
  } : null].filter(Boolean) as {
    id: string;
    tone: 'warn' | 'danger' | 'brand' | 'neutral';
    icon: typeof RocketIcon;
    title: string;
    detail: string;
    to: string;
    action: string;
  }[];
  return <div className="space-y-6" data-id="element-5546" data-mp="3hcrw97odcgcm">
      <PageHeader eyebrow="Administration" title="What needs attention" description="Scoped to your operating context. Nothing here is a summary of the whole platform unless your access covers the whole platform." actions={<Badge tone="ok" plain data-id="element-5548" data-mp="2tme65mpcgrko">
            Production environment
          </Badge>} data-id="element-5547" data-mp="33m51trpn5cmc" />

      {/* Counted from the records this administrator can actually see, so a
          scoped administrator is never shown a platform-wide figure they have no
          authority over. Nothing here is a stored metric. */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-id="element-5549" data-mp="2oqpavvkcxwu6">
        <KpiCard label="Waiting on an approver" value={canSeeApprovals ? pendingApprovals.length : undefined} meaning={canSeeApprovals ? 'Requests in your scope that cannot proceed until someone other than the requester decides.' : 'Your roles do not include an approval permission, so this is not yours to see.'} icon={ClipboardCheckIcon} tone={pendingApprovals.length ? 'warn' : 'neutral'} to={canSeeApprovals ? '/admin/governance' : undefined} linkLabel="Open approvals" data-id="element-5550" data-mp="iwflfa9ahirj" />
        <KpiCard label="Administrators" value={activeStaff.length} meaning={`Active staff records. ${noMfa.length} without MFA.`} icon={UsersIcon} tone={noMfa.length ? 'warn' : 'ok'} to="/admin/governance" linkLabel="Open access review" data-id="element-5551" data-mp="2o4n5bvdb04sv" />
        <KpiCard label="Open support tickets" value={openTickets.length} meaning="Raised by people using the platform and not yet resolved or closed." icon={HeadphonesIcon} tone={openTickets.length ? 'brand' : 'ok'} to="/admin/support" linkLabel="Open support desk" data-id="element-5552" data-mp="297w8ntv0l76y" />
        <KpiCard label="Refused attempts" value={deniedInScope.length} meaning="Actions the authorization engine refused. Recorded exactly like successes." icon={ShieldAlertIcon} tone={deniedInScope.length ? 'warn' : 'ok'} series={deniedSeries} to="/admin/audit" linkLabel="Open audit log" data-id="element-5553" data-mp="jw919s8r5cd1" />
      </div>

      <Panel title="Your access" description="A role says what you may do. A scope says where. They are evaluated separately." data-id="element-5554" data-mp="xmz0a1i1f7j">
        <ScopeSwitcher data-id="element-5555" data-mp="5vyasqbctzse" />
        <dl className="mt-5 grid gap-4 border-t border-line pt-4 sm:grid-cols-3" data-id="element-5556" data-mp="awcj89gvbbqy">
          <div data-id="element-5557" data-mp="au9kn11ngaam">
            <dt className="text-body-sm text-ink-muted" data-id="element-5558" data-mp="1hcerso2k4ohd">Signed in as</dt>
            <dd className="mt-0.5 text-body font-semibold text-ink" data-id="element-5559" data-mp="1heiu51kt5ffj">
              {me.name} <span className="cz-num font-normal text-ink-muted" data-id="element-5560" data-mp="1m9y4hjjlwt77">{me.handle}</span>
            </dd>
          </div>
          <div data-id="element-5561" data-mp="2dc3rrjzgqdpn">
            <dt className="text-body-sm text-ink-muted" data-id="element-5562" data-mp="2daj3ud7nlt0r">Active assignments</dt>
            <dd className="mt-0.5 text-body font-semibold text-ink" data-id="element-5563" data-mp="2i9m2nfx1j30a">
              {me.assignments.filter(assignment => assignment.state === 'active').length}
            </dd>
          </div>
          <div data-id="element-5564" data-mp="2r2z1eyhscyua">
            <dt className="text-body-sm text-ink-muted" data-id="element-5565" data-mp="2r5v2uwsue5nh">Looking at</dt>
            <dd className="mt-0.5 text-body font-semibold text-ink" data-id="element-5566" data-mp="1uf2hpkr1xneu">{scopeLabel(activeScope)}</dd>
          </div>
        </dl>
      </Panel>

      {attention.length === 0 ? <Panel title="Nothing outstanding" data-id="element-5567" data-mp="23dwk5rr2d80b">
          <EmptyState icon={<ClipboardCheckIcon className="h-5 w-5" data-id="element-5569" data-mp="28d0vanxjtfta" />} title="No work waiting on you" description="Nothing in your scope needs an approval, review or decision right now." data-id="element-5568" data-mp="23gaetzhu0i7c" />
        </Panel> : <ul className="grid gap-4 lg:grid-cols-2" data-id="element-5570" data-mp="3jregtm4txj65">
          {attention.map(item => {
        const Icon = item.icon;
        return <li key={item.id} data-id="element-5571" data-mp="3ty77m8pngadi">
                <Panel bodyClassName="p-5" data-id="element-5572" data-mp="3owzzu9nibb6u">
                  <div className="flex items-start gap-3" data-id="element-5573" data-mp="3oy1lt9629i3c">
                    <span aria-hidden className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-canvas text-ink-muted" data-id="element-5574" data-mp="3k5rtjmhh45xw">
                      <Icon className="h-5 w-5" data-id="element-5575" data-mp="3b6o7qaqx9cfp" />
                    </span>
                    <div className="min-w-0" data-id="element-5576" data-mp="366impdf5qo6l">
                      <div className="flex flex-wrap items-center gap-2" data-id="element-5577" data-mp="365h1ig4fglb5">
                        <p className="text-body-lg font-semibold text-ink" data-id="element-5578" data-mp="318gthzo0p8he">{item.title}</p>
                        {item.tone === 'danger' && <Badge tone="danger" icon={<AlertTriangleIcon className="h-4 w-4" data-id="element-5580" data-mp="zgu6e978xxq7" />} data-id="element-5579" data-mp="zgc3wutzqn9u">
                            Blocker
                          </Badge>}
                      </div>
                      <p className="mt-1.5 max-w-[62ch] text-body-sm text-ink-muted" data-id="element-5581" data-mp="2owxwwy5s6ntr">{item.detail}</p>
                      <Button variant="secondary" size="sm" className="mt-3" onClick={() => navigate(item.to)} data-id="element-5582" data-mp="2topqzio5bbon">
                        {item.action}
                      </Button>
                    </div>
                  </div>
                </Panel>
              </li>;
      })}
        </ul>}

      {/* Each widget owns its own loading, empty and error state, and is wrapped
          so one failure cannot take the console down with it. */}
      <div className="grid gap-4 xl:grid-cols-3" data-id="element-5583" data-mp="33mc5w55g338m">
        <WidgetBoundary title="Recorded administrative activity" data-id="element-5584" data-mp="33l176jquwlod">
          <div className="xl:col-span-2" data-id="element-5585" data-mp="1n7kp6phnvkf5">
            <ActivityChartWidget data-id="element-5586" data-mp="1n6ssfbcbvgna" />
          </div>
        </WidgetBoundary>
        <WidgetBoundary title="Quick actions" data-id="element-5587" data-mp="1s88cb02rrh43">
          <QuickActionsWidget data-id="element-5588" data-mp="1s4ud20pejo0v" />
        </WidgetBoundary>
      </div>

      <div className="grid gap-4 xl:grid-cols-3" data-id="element-5589" data-mp="1jolm6a0v0e5h">
        <WidgetBoundary title="Recent activity" data-id="element-5590" data-mp="1fouwk9n2k42j">
          <div className="xl:col-span-2" data-id="element-5591" data-mp="1fo1mayowiwfn">
            <ActivityWidget data-id="element-5592" data-mp="1to430hlh3jtb" />
          </div>
        </WidgetBoundary>
        <div className="space-y-4" data-id="element-5593" data-mp="1tm9s7w3x182y">
          <WidgetBoundary title="System health" data-id="element-5594" data-mp="1opj3ylhyj66g">
            <HealthWidget data-id="element-5595" data-mp="1oq2kbxrylmid" />
          </WidgetBoundary>
          <WidgetBoundary title="Design variants" data-id="element-5596" data-mp="10z5k3i9j0dmy">
            <DesignVariantWidget data-id="element-5597" data-mp="10zyk80dzc4w4" />
          </WidgetBoundary>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-2" data-id="element-5598" data-mp="w26sb6dc4irx">
        <Panel title="Paused capabilities" description="Paused, never deleted. Existing records continue through controlled recovery." bodyClassName="p-0" data-id="element-5599" data-mp="1ap8bxf4wvljp">
          {paused.length === 0 ? <p className="px-5 py-4 text-body-sm text-ink-muted" data-id="element-5600" data-mp="i7w914k3xgt9">Nothing is currently paused.</p> : <ul className="divide-y divide-line" data-id="element-5601" data-mp="d9kxmpe5yy8x">
              {paused.map(item => <li key={item.id} className="px-5 py-3.5" data-id="element-5602" data-mp="3cpelx0kngw8w">
                  <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-5603" data-mp="3hmyb2vivytfm">
                    <p className="text-body font-medium text-ink" data-id="element-5604" data-mp="3loteq6oahiwk">{item.label}</p>
                    <Badge tone="warn" data-id="element-5605" data-mp="3lmp3f3o29hm9">Paused</Badge>
                  </div>
                  <p className="mt-1 max-w-[62ch] text-body-sm text-ink-muted" data-id="element-5606" data-mp="3qh2yt62q3oqx">{item.effect}</p>
                </li>)}
            </ul>}
        </Panel>

        <Panel title="Invitations" bodyClassName="p-0" data-id="element-5607" data-mp="478tap78s4mp">
          <ul className="divide-y divide-line" data-id="element-5608" data-mp="97eouggmuba2">
            <li className="flex items-center justify-between px-5 py-3.5" data-id="element-5609" data-mp="98ps8f661h0n">
              <p className="text-body text-ink" data-id="element-5610" data-mp="d9i137b4ljwe">Pending acceptance</p>
              <p className="cz-num text-body-lg font-semibold text-ink" data-id="element-5611" data-mp="d6nepa8jxp0c">{pendingInvites.length}</p>
            </li>
            <li className="flex items-center justify-between px-5 py-3.5" data-id="element-5612" data-mp="2f037aco79dxy">
              <p className="text-body text-ink" data-id="element-5613" data-mp="2f269dv2mmxj1">Expired without acceptance</p>
              <p className="cz-num text-body-lg font-semibold text-ink" data-id="element-5614" data-mp="27i52glgyqr12">{expiredInvites.length}</p>
            </li>
          </ul>
          <p className="border-t border-line px-5 py-3.5 text-body-sm text-ink-muted" data-id="element-5615" data-mp="1xr0nvmv7986r">
            An expired invitation grants nothing. The token is single-use and stops working on its
            expiry whether or not anyone revokes it.
          </p>
        </Panel>
      </div>
    </div>;
}